<template>
  <p-dialog 
    :visible.sync="visible" 
    :modal="true" 
    :closable="true"
    :showHeader="false"
    class="product-selection-dialog medium-dialog"
  >
    <div class="pageContainerParent">
      <!-- Close Button -->
      <div class="close-icon-container" @click="handleCancel">
          <i class="pi pi-times" style="font-size: 1.2rem; color: #666; cursor: pointer;"></i>
      </div>

      <!-- Logo -->
      <div class="p-d-flex p-jc-center" style="margin-bottom: 1rem; margin-top: 1rem;">
        <img src="/images/logo-login.png" alt="Protrader login" style="max-width: 180px; display: block" />
      </div>

      <!-- Header -->
      <div class="p-d-flex p-flex-column p-jc-center p-ai-center" style="margin-bottom: 1.5rem;">
        <span style="font-size: 26px; font-weight: 600; color: #300003;">Select Your Plan</span>
      </div>

      <!-- Monthly/Annual Toggle -->
      <div class="toggle-container">
        <div class="toggle-wrapper">
          <button 
            :class="['toggle-btn', { active: billingCycle === 'monthly' }]" 
            @click="billingCycle = 'monthly'"
          >
            Monthly
          </button>
          <button 
            :class="['toggle-btn', { active: billingCycle === 'annual' }]" 
            @click="billingCycle = 'annual'"
          >
            Annual
          </button>
        </div>
      </div>

      <!-- Product Cards -->
      <div class="product-cards">
        <div 
          v-for="(product, index) in products" 
          :key="index"
          class="product-card"
        >
          <!-- Currency Dropdown -->
          <div class="card-currency-select">
            <p-dropdown
              v-model="product.selectedCurrency"
              :options="currencies"
              optionLabel="country"
              placeholder="Select Currency"
              class="currency-dropdown"
              @click.native.stop
            />
          </div>

          <div class="card-content">
            <h3><b>{{ product.title }}</b></h3>
            <p class="subtitle">{{ product.subtitle }}</p>
            
            <div class="price-display">
               <p class="price">{{ product.selectedCurrency.symbol }}{{ getPrice(product) }}</p>
               <p class="period">{{ product.selectedCurrency.name }} / Month</p>
            </div>

             <ul class="features-list">
              <li v-for="(feature, fIndex) in product.features" :key="fIndex">
                <i class="pi pi-check-circle"></i> {{ feature }}
              </li>
            </ul>
          </div>

          <div class="card-actions">
            <button 
              type="button" 
              class="action-btn primary-btn" 
              @click="confirmProduct(product)"
            >
              Get Pro Now <i class="pi pi-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="errorText" style="color:#F20E17; text-align: center; margin: 10px 0; font-size: 14px;">
        ⚠ {{ errorMessage }}
      </div>

    </div>
  </p-dialog>
</template>

<script>
import config from '../../../config';

export default {
  name: 'ProductSelectionPopup',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    preSelectedCurrency: {
      type: String,
      default: null
    },
    preSelectedProduct: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      billingCycle: 'monthly',
      errorMessage: '',
      
      currencies: [
        { name: 'USD', symbol: '$', code: 'USD', country: 'United States' },
        { name: 'AUD', symbol: '$', code: 'AUD', country: 'Australia' },
      ],

      products: [
        {
          id: 'pro_noasx',
          title: 'PRO (No ASX)',
          subtitle: 'ProTrader® without ASX data.',
          features: [
            'Unlimited indicators',
            'Exclusive guru scanners',
            'Learn with ProTrader Academy',
            'End of day data',
            '3 Exchanges – North America (AMEX, NASDAQ, NYSE)',
            'Fast full support',
            'Up to 20 years of data history',
            'Free access to our exclusive trading room'
          ],
          selectedCurrency: { name: 'USD', symbol: '$', code: 'USD', country: 'United States' }, // Default
          pricing: {
             USD: { monthly: 19.00, yearly: 17.41, monthlyCode: 'M_noASX', yearlyCode: 'Y_noASX', monthlyId: config.Monthly_noASX_priceId, yearlyId: config.Yearly_noASX_priceId },
             AUD: { monthly: 28.00, yearly: 26.00, monthlyCode: 'M_noASX', yearlyCode: 'Y_noASX', monthlyId: config.Monthly_noASX_AUD_priceId, yearlyId: config.Yearly_noASX_AUD_priceId }
          }
        },
        {
          id: 'pro',
          title: 'PRO',
          subtitle: 'ProTrader® software was developed by professional traders to allow users to find hot stocks in seconds.',
          features: [
            'Unlimited Indicators',
            'Exclusive guru scanners',
            'Learn with ProTrader Academy',
            'End of day data',
            '5 Exchanges – Australia (ASX), Singapore (SGX), North America (AMEX, NASDAQ, NYSE)',
            'Fast full support',
            'Up to 20 years of data history',
            'Free access to our exclusive trading room'
          ],
          // selectedCurrency: { name: 'AUD', symbol: '$', code: 'AUD', country: 'Australia' }, // Default
          selectedCurrency: { name: 'USD', symbol: '$', code: 'USD', country: 'United States' }, // Default
          pricing: {
             USD: { monthly: 49.00, yearly: 44.92, monthlyCode: 'M', yearlyCode: 'Y', monthlyId: config.Monthly_priceId, yearlyId: config.Yearly_priceId },
             AUD: { monthly: 69.00, yearly: 63.25, monthlyCode: 'M', yearlyCode: 'Y', monthlyId: config.Monthly_AUD_priceId, yearlyId: config.Yearly_AUD_priceId }
          }
        },
        
      ]
    }
  },
  methods: {
    handlePreSelection() {
       if (this.preSelectedCurrency) {
           const currencyCode = this.preSelectedCurrency.toUpperCase();
           const found = this.currencies.find(c => c.code === currencyCode);
           if (found) {
               // Update default currency for ALL products to match pre-selection
               this.products.forEach(p => {
                   p.selectedCurrency = found;
               });
           }
       }

       if (this.preSelectedProduct) {
           const prod = this.preSelectedProduct;
           
           // Determine Billing Cycle
           if (prod.includes('Y') || prod.includes('yearly')) {
               this.billingCycle = 'annual';
           } else {
               this.billingCycle = 'monthly';
           }

           // Note: We don't "select" the product anymore since we removed the selection state 
           // and moved to direct button action. However, setting the billing cycle 
           // ensures the view defaults to the correct tab.
           // We might also want to ensure the specific product related to the code (e.g. NoASX) 
           // has its currency set if implied, but the global currency handler above covers it.
       }
    },
    getPrice(product) {
       const currencyCode = product.selectedCurrency.code;
       const priceObj = product.pricing[currencyCode];
       if (!priceObj) return 'N/A';
       
       const price = this.billingCycle === 'monthly' ? priceObj.monthly : priceObj.yearly;
       return Number(price).toString();
    },
    confirmProduct(product) {
       const currencyCode = product.selectedCurrency.code;
       const priceObj = product.pricing[currencyCode];
       
       if (!priceObj) {
           this.errorMessage = 'Pricing not available for this selection.';
           return;
       }

       let selectedProductCode = '';
       let selectedPriceId = '';

       if (this.billingCycle === 'monthly') {
           selectedProductCode = priceObj.monthlyCode;
           selectedPriceId = priceObj.monthlyId;
       } else {
           selectedProductCode = priceObj.yearlyCode;
           selectedPriceId = priceObj.yearlyId;
       }
       
       this.errorMessage = '';
       
       this.$emit('product-selected', {
            productType: selectedProductCode,
            priceId: selectedPriceId,
            currency: currencyCode
       });
    },
    handleCancel() {
      this.errorMessage = '';
      this.$emit('cancel');
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.handlePreSelection()
      }
    }
  }
}
</script>

<style scoped>
.pageContainerParent {
  padding: 0 30px;
  width: 100%;
}

.close-icon-container {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
}

/* Toggle Switch */
.toggle-container {
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
}
.toggle-wrapper {
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 50px;
    padding: 4px;
    display: flex;
}
.toggle-btn {
    background: transparent;
    border: none;
    color: #666;
    padding: 10px 24px;
    border-radius: 50px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
}
.toggle-btn.active {
    background: #000;
    color: #fff;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.product-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  align-items: stretch;
  margin-bottom: 20px;
  width: 100%;
}

.product-card {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  min-width: 250px;
  padding: 2.5rem 1.5rem;
  background: #eee;
  border: 1px solid #ccc;
  border-radius: 16px;
  flex: 1;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
}

.product-card:hover {
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.3);
  transform: translateY(-2px);
}

.card-currency-select {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  margin-bottom: 2rem;
}

.card-content h3 {
  color: #000;
  font-size: 18px; 
  margin-bottom: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.price-display {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
}

.price {
  color: #000;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 0px;
  line-height: 1.2;
}

.period {
  color: #555;
  font-size: 14px;
}

.subtitle {
    color: #555;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.4;
    min-height: 40px; 
}

.features-list {
    list-style: none;
    padding: 0;
    margin: 1.5rem 0 0 0;
    text-align: left;
    width: 100%;
}

.features-list li {
    margin-bottom: 0.6rem;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: #000;
    font-size: 0.9rem;
}

.features-list li i {
    color: #db2828;
    font-size: 1rem;
    margin-top: 2px;
}

.card-actions {
    margin-top: auto;
    width: 100%;
}

.action-btn {
    width: 100%;
    padding: 1rem;
    border-radius: 50px;
    border: none;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    transition: transform 0.2s, box-shadow 0.2s;
}

.primary-btn {
    background: #db2828;
    color: white;
    background: linear-gradient(90deg, #d32f2f 0%, #b71c1c 100%);
    box-shadow: 0 4px 15px rgba(219, 40, 40, 0.4);
}

.action-btn:hover {
    transform: translateY(-2px);
    filter: brightness(1.1);
}

.errorText {
  font-family: -apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif;
}

/* PrimeVue Overrides for Card Dropdown */
:deep(.currency-dropdown) {
    background: #ffffff;
    border: none;
    border-radius: 20px;
    height: 35px;
    display: flex;
    align-items: center;
}
:deep(.p-dropdown-label) {
    color: #000;
    font-weight: bold;
    font-size: 0.9rem;
}
:deep(.p-dropdown-trigger) {
    color: #000;
}

@media screen and (max-width: 768px) {
  .product-cards {
    flex-direction: column;
    align-items: center;
  }
  
  .product-card {
    width: 100%;
    max-width: 100%;
  }
}
</style>
